import { navigate } from "@reach/router";
import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Calendar from "images/icons/calender-calendly-icon.inline.svg";

import { useTheme } from "../../../context/ThemeContext";
import { processForm } from "../../../utils/processForm";
import { blockedCalendlyConfig } from "../../../utils/sharedDatasets";
import EmbedCalendlyEvent from "../../embeds/calendly";
import CtaButton from "../../shared/buttons/ctaButton";
import ProgramRating from "../../shared/programRating";
import { StatusComp } from "../../shared/statusComp";

const CalendlyBridgeStep = ({
  programs,
  extraDetails,
  redirectAfterSubmission = true
}) => {
  const [formStatus, setFormStatus] = useState("calendly");
  const [programID, setProgramID] = useState("");

  const [redirectParams, setRedirectParams] = useState();

  const [calendlyStatus, setCalendlyStatus] = useState("gold");
  const [prefillCalendlyData, setPrefillCalendlyData] = useState({});
  const [calendlyEvent, setCalendlyEvent] = useState();

  const defaultValues = {
    call_back_now_request: false
  };

  const formOptions = {
    defaultValues
    //resolver: yupResolver(validationSchema)
  };
  const form = useForm(formOptions);

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    async (data) => {
      try {
        window.scrollTo(0, 0);

        const formFields = {
          submit_type: "call_back_now_request",
          form_type: "complete_application"
        };

        //check state data
        let forwardingStateData = {
          ...formFields
        };

        if (typeof window !== "undefined") {
          const collectHistoryData = window?.history?.state ?? {};
          forwardingStateData = {
            ...collectHistoryData,
            hide_calendly: true,
            submit_type: "call_back_now_request",
            form_type: "complete_application"
          };
        }

        // console.debug("Step2  onSubmit >> formFields", forwardingStateData);

        setFormStatus("processing");
        await processForm({
          formdata: forwardingStateData
        });

        reset();

        const redirectUrl = [
          "/thank-you-for-your-complete-application/?",
          new Date()?.getTime()
        ].join("");

        await navigate(redirectUrl, { state: forwardingStateData });
      } catch (err) {
        console.error("Error requestForm :: ", err);
      }
    },
    [reset]
  );

  const { currentSiteMeta } = useTheme();

  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const currentUrl = new URL(location);

    const lead_id = currentUrl.searchParams.get("lead_id");
    const fundraising = currentUrl.searchParams.get("fundraising");
    const programCode = currentUrl.searchParams.get("program_id");
    const departureDate = currentUrl.searchParams.get("departure_date");
    const duration = currentUrl.searchParams.get("duration");
    const referring_form_type = currentUrl.searchParams.get(
      "referring_form_type"
    );

    const bridgeData = {
      lead_id: lead_id,
      duration: duration,
      departure_date: departureDate,
      program_code: programCode,
      program_id: programCode,
      fundraising: fundraising,
      referring_form_type: referring_form_type
    };

    setRedirectParams(bridgeData);

    if (programCode) setProgramID(programCode);

    if (currentUrl.search.includes("gvivirtual")) {
      setCalendlyStatus("silver");
    }

    if (typeof window !== "undefined") {
      const collectedHistoryData = window.history.state;

      const prefillCalendly = {
        name: [
          collectedHistoryData?.first_name,
          collectedHistoryData?.last_name
        ].join(" "),
        email: collectedHistoryData?.email_address,
        location: collectedHistoryData?.phone_number
      };

      setPrefillCalendlyData(prefillCalendly);

      //this is a fallback that is needed for when the url params we are expecting do not populate
      if (collectedHistoryData?.form_type?.includes("gvivirtual")) {
        setCalendlyStatus("silver");
      }
    }
  }, []);

  useEffect(() => {
    /**
     *
     * @description
     *
     * @param {Object} calendlyEvent - The calendly event object
     * @param {string} calendlyEvent.event - The calendly event type
     * @param {Object} calendlyEvent.payload - The calendly event payload data
     *
     * events
     *  - calendly.event_type_viewed
     *  - calendly.date_and_time_selected
     *  - calendly.event_scheduled
     *
     * @returns {void}
     *
     * **/

    const collectedHistoryData = window?.history?.state ?? {};

    if (
      redirectAfterSubmission &&
      calendlyEvent?.event === "calendly.event_scheduled"
    ) {
      console.log("redirecting after event_scheduled", collectedHistoryData);
      navigate(
        collectedHistoryData?.redirectUrl ||
          [
            "/thank-you-for-your-complete-application/?__",
            new Date()?.getTime()
          ].join(""),
        { state: collectedHistoryData }
      );
    }
  }, [calendlyEvent, redirectAfterSubmission]);

  return (
    <div className="px-5 py-10 step-wrapper md:px-16">
      {formStatus === "processing" && (
        <>
          <div className="w-3/4 py-12 m-auto ">
            <StatusComp
              expText={"Just a moment..."}
              statusText={`We’re making sure we have everything we need before connecting you with one of our team members!`}
              status={formStatus}
            />
          </div>
        </>
      )}

      {formStatus === "calendly" && (
        <>
          <div className="w-full">
            <FormProvider {...form}>
              <form
                id={"application-step-2"}
                className="w-full text-center md:text-left md:pb-8"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="flex flex-col">
                  <div
                    className={
                      "flex flex-col sm:flex-row items-center sm:items-center sm:justify-between gap-3"
                    }
                  >
                    <h1
                      className={`font-light shrink-0 text-white`}
                      dangerouslySetInnerHTML={{
                        __html: blockedCalendlyConfig.title
                      }}
                    />

                    <div className="block">
                      <ProgramRating
                        outerContainerClass={
                          "items-end justify-center md:justify-end py-2 sm:py-0"
                        }
                        ratingIconClass={"fill-white h-5"}
                        ratingLogoClass={"fill-white h-5"}
                        darkRatingIconClass="h-5 w-auto fill-white"
                      />
                    </div>
                  </div>
                  <div
                    className="pb-5 pt-3 text-base sm:text-xl font-light text-white text-center sm:text-left"
                    dangerouslySetInnerHTML={{
                      __html: `${blockedCalendlyConfig.titleText} <a class="font-bold" href="tel:${intlNumber}" title="${intlNumber}" rel="nofollow">${intlNumber}.</a>`
                    }}
                  />
                  <CtaButton
                    ctaLabel={"Call me now"}
                    preset={"primary"}
                    labelClass="text-base leading-genericCta pr-2 font-bold"
                    extraButtonStyle={"w-full sm:w-56"}
                    arrowClass={
                      "w-3 h-3 stroke-white dark:stroke-gray-darker dark:group-hover:stroke-green-standard group-hover:stroke-black-lighter ml-2"
                    }
                    showAsButton={true}
                  />
                </div>
              </form>
            </FormProvider>

            <div className="flex flex-col pt-6">
              <div
                className={
                  "flex flex-col sm:flex-row items-center sm:items-center sm:justify-start gap-3"
                }
              >
                <Calendar
                  className={`h-9 w-9 shrink-0 text-white fill-white`}
                />
                <h1
                  className={`font-light shrink-0 text-white`}
                  dangerouslySetInnerHTML={{
                    __html: blockedCalendlyConfig.heading
                  }}
                />
              </div>
              <div
                className="pb-5 pt-3 text-base sm:text-xl font-light text-white text-center sm:text-left"
                dangerouslySetInnerHTML={{
                  __html: blockedCalendlyConfig.descAltText
                }}
              />
            </div>

            <div className="mt-0">
              {programID && calendlyStatus === "gold" ? (
                <EmbedCalendlyEvent
                  {...blockedCalendlyConfig.gold}
                  prefill={prefillCalendlyData}
                  calendlyEventListener={setCalendlyEvent}
                />
              ) : (
                <EmbedCalendlyEvent
                  {...blockedCalendlyConfig.default}
                  prefill={prefillCalendlyData}
                  calendlyEventListener={setCalendlyEvent}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default CalendlyBridgeStep;
